import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Table, Alert, Spinner, Card, Row, Col, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaCamera, FaUpload, FaCheck, FaTimes, FaReceipt, FaFilePdf } from 'react-icons/fa';

const ReceiptScanner = ({ projectId, token, onMaterialsAdded }) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isCheckingFeature, setIsCheckingFeature] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [editableItems, setEditableItems] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isPdfFile, setIsPdfFile] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);
  
  // Check if receipt scanning is enabled for this customer
  useEffect(() => {
    checkFeatureStatus();
  }, []);
  
  const checkFeatureStatus = async () => {
    try {
      const response = await fetch('/api/receipts/feature-status', {
        headers: {
          'x-auth-token': token
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsEnabled(data.enabled);
      }
    } catch (err) {
      console.error('Error checking feature status:', err);
    } finally {
      setIsCheckingFeature(false);
    }
  };
  
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type - allow images and PDFs
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';
    
    if (!isImage && !isPdf) {
      toast.error('Please select an image or PDF file');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB');
      return;
    }
    
    setFileName(file.name);
    setIsPdfFile(isPdf);
    
    // Create preview for images only
    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
    
    // Upload and process
    await uploadAndProcessReceipt(file);
  };
  
  const uploadAndProcessReceipt = async (file) => {
    setIsUploading(true);
    setIsProcessing(true);
    setScanResult(null);
    setEditableItems([]);
    
    const formData = new FormData();
    formData.append('receipt', file);
    formData.append('projectId', projectId);
    
    try {
      const response = await fetch('/api/receipts/scan', {
        method: 'POST',
        headers: {
          'x-auth-token': token
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to process receipt');
      }
      
      setScanResult(data);
      
      // Initialize editable items with ability to exclude
      if (data.data && data.data.lineItems) {
        const items = data.data.lineItems.map((item, index) => ({
          ...item,
          id: index,
          exclude: false,
          // Default unit based on quantity
          unit: item.quantity > 1 ? 'each' : 'each'
        }));
        
        // Add tax as a line item if present
        if (data.data.totals?.tax && data.data.totals.tax > 0) {
          items.push({
            id: items.length,
            sku: null,
            description: 'Sales Tax',
            quantity: 1,
            unitPrice: data.data.totals.tax,
            totalPrice: data.data.totals.tax,
            unit: 'each',
            exclude: false,
            isTax: true  // Flag to style differently
          });
        }
        
        setEditableItems(items);
      }
      
      toast.success(`Receipt processed! Found ${data.itemCount} items.`);
      
    } catch (err) {
      console.error('Error processing receipt:', err);
      toast.error(err.message || 'Failed to process receipt');
    } finally {
      setIsUploading(false);
      setIsProcessing(false);
    }
  };
  
  const handleItemChange = (index, field, value) => {
    const updated = [...editableItems];
    updated[index][field] = value;
    
    // Recalculate total if quantity or unit price changed
    if (field === 'quantity' || field === 'unitPrice') {
      const qty = parseFloat(updated[index].quantity) || 0;
      const price = parseFloat(updated[index].unitPrice) || 0;
      updated[index].totalPrice = qty * price;
    }
    
    setEditableItems(updated);
  };
  
  const toggleItemExclude = (index) => {
    const updated = [...editableItems];
    updated[index].exclude = !updated[index].exclude;
    setEditableItems(updated);
  };
  
  const handleApprove = async () => {
    const itemsToAdd = editableItems.filter(item => !item.exclude);
    
    if (itemsToAdd.length === 0) {
      toast.error('No items selected to add');
      return;
    }
    
    try {
      const response = await fetch(`/api/receipts/${scanResult.scanId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({ lineItems: itemsToAdd })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to approve receipt');
      }
      
      toast.success(`${data.materialsCreated} materials added successfully!`);
      
      // Reset and close
      resetModal();
      
      // Notify parent to refresh materials
      if (onMaterialsAdded) {
        onMaterialsAdded();
      }
      
    } catch (err) {
      console.error('Error approving receipt:', err);
      toast.error(err.message || 'Failed to add materials');
    }
  };
  
  const resetModal = () => {
    setShowModal(false);
    setScanResult(null);
    setEditableItems([]);
    setPreviewUrl(null);
    setIsPdfFile(false);
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const openCamera = () => {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('capture', 'environment');
      fileInputRef.current.setAttribute('accept', 'image/*');
      fileInputRef.current.click();
    }
  };
  
  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.removeAttribute('capture');
      fileInputRef.current.setAttribute('accept', 'image/*,application/pdf');
      fileInputRef.current.click();
    }
  };
  
  // Don't show anything while checking feature status
  if (isCheckingFeature) {
    return null;
  }
  
  // Don't show if feature is not enabled
  if (!isEnabled) {
    return null;
  }
  
  const includedItemsTotal = editableItems
    .filter(item => !item.exclude)
    .reduce((sum, item) => sum + (parseFloat(item.totalPrice) || 0), 0);
  
  return (
    <>
      <Button 
        variant="success" 
        size="sm" 
        onClick={() => setShowModal(true)}
        className="ms-2"
      >
        <FaReceipt className="me-1" />
        Scan Receipt
      </Button>
      
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*,application/pdf"
        style={{ display: 'none' }}
      />
      
      {/* Receipt Scanner Modal */}
      <Modal show={showModal} onHide={resetModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaReceipt className="me-2" />
            Scan Receipt
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!scanResult ? (
            // Upload Section
            <div className="text-center py-4">
              {isProcessing ? (
                <div>
                  <Spinner animation="border" variant="primary" className="mb-3" />
                  <h5>Processing receipt...</h5>
                  <p className="text-muted">AI is extracting items from your receipt</p>
                  {isPdfFile ? (
                    <div className="mt-3 p-4 bg-light rounded d-inline-block">
                      <FaFilePdf size={48} className="text-danger mb-2" />
                      <p className="mb-0 text-muted">{fileName}</p>
                    </div>
                  ) : previewUrl && (
                    <img 
                      src={previewUrl} 
                      alt="Receipt preview" 
                      style={{ maxHeight: '200px', opacity: 0.5 }} 
                      className="mt-3 rounded"
                    />
                  )}
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <FaReceipt size={48} className="text-muted mb-3" />
                    <h5>Upload a Receipt</h5>
                    <p className="text-muted">
                      Take a photo, upload an image, or upload a PDF receipt from Home Depot, Lowe's, or any store.
                      <br />AI will automatically extract the items and prices.
                    </p>
                  </div>
                  
                  <div className="d-flex justify-content-center gap-3">
                    <Button variant="primary" size="lg" onClick={openCamera}>
                      <FaCamera className="me-2" />
                      Take Photo
                    </Button>
                    <Button variant="outline-primary" size="lg" onClick={openFilePicker}>
                      <FaUpload className="me-2" />
                      Upload File
                    </Button>
                  </div>
                  
                  <div className="mt-4">
                    <small className="text-muted">
                      Supported formats: JPG, PNG, GIF, WebP, <strong>PDF</strong> • Max size: 10MB
                    </small>
                  </div>
                </>
              )}
            </div>
          ) : (
            // Results Section
            <div>
              <Row>
                {/* Receipt Image/PDF Preview */}
                <Col md={4}>
                  <Card>
                    <Card.Header>Receipt File</Card.Header>
                    <Card.Body className="text-center">
                      {isPdfFile ? (
                        <div className="p-4 bg-light rounded">
                          <FaFilePdf size={64} className="text-danger mb-2" />
                          <p className="mb-0 text-muted">{fileName}</p>
                          <small className="text-muted">PDF Document</small>
                        </div>
                      ) : previewUrl ? (
                        <img 
                          src={previewUrl} 
                          alt="Receipt" 
                          style={{ maxWidth: '100%', maxHeight: '400px' }} 
                          className="rounded"
                        />
                      ) : (
                        <div className="p-4 bg-light rounded">
                          <FaReceipt size={64} className="text-muted mb-2" />
                          <p className="mb-0 text-muted">Receipt uploaded</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                  
                  {/* Store Info */}
                  {scanResult.data?.store && (
                    <Card className="mt-3">
                      <Card.Header>Store Information</Card.Header>
                      <Card.Body>
                        <strong>{scanResult.data.store.name}</strong>
                        <br />
                        <small className="text-muted">
                          {scanResult.data.store.address}<br />
                          {scanResult.data.store.city}, {scanResult.data.store.state} {scanResult.data.store.zip}
                        </small>
                        {scanResult.data.transaction?.date && (
                          <>
                            <hr />
                            <small>
                              <strong>Date:</strong> {scanResult.data.transaction.date}
                            </small>
                          </>
                        )}
                        {scanResult.isReturn && (
                          <>
                            <hr />
                            <Badge bg="warning" text="dark">RETURN</Badge>
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  )}
                </Col>
                
                {/* Extracted Items */}
                <Col md={8}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <span>Extracted Items</span>
                      <Badge bg="primary">
                        {editableItems.filter(i => !i.exclude).length} of {editableItems.length} selected
                      </Badge>
                    </Card.Header>
                    <Card.Body>
                      {editableItems.length > 0 ? (
                        <>
                          <Alert variant="info" className="py-2">
                            <small>Review and edit items below. Uncheck items you don't want to add.</small>
                          </Alert>
                          
                          <div className="table-responsive">
                            <Table size="sm" hover>
                              <thead>
                                <tr>
                                  <th style={{ width: '40px' }}>Add</th>
                                  <th>Description</th>
                                  <th style={{ width: '60px' }}>SKU</th>
                                  <th style={{ width: '70px' }}>Qty</th>
                                  <th style={{ width: '90px' }}>Unit Price</th>
                                  <th style={{ width: '90px' }}>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {editableItems.map((item, index) => (
                                  <tr 
                                    key={index}
                                    className={`${item.exclude ? 'text-muted bg-light' : ''} ${item.isTax ? 'table-warning' : ''}`}
                                  >
                                    <td>
                                      <Form.Check
                                        type="checkbox"
                                        checked={!item.exclude}
                                        onChange={() => toggleItemExclude(index)}
                                      />
                                    </td>
                                    <td>
                                      {item.isTax ? (
                                        <strong className="text-warning-emphasis">
                                          <Badge bg="warning" text="dark" className="me-2">TAX</Badge>
                                          {item.description}
                                        </strong>
                                      ) : (
                                        <Form.Control
                                          type="text"
                                          size="sm"
                                          value={item.description}
                                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                          disabled={item.exclude}
                                        />
                                      )}
                                    </td>
                                    <td>
                                      <small className="text-muted">{item.sku || '-'}</small>
                                    </td>
                                    <td>
                                      {item.isTax ? (
                                        <span>1</span>
                                      ) : (
                                        <Form.Control
                                          type="number"
                                          size="sm"
                                          step="0.01"
                                          value={item.quantity}
                                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                          disabled={item.exclude}
                                        />
                                      )}
                                    </td>
                                    <td>
                                      {item.isTax ? (
                                        <span>${(parseFloat(item.unitPrice) || 0).toFixed(2)}</span>
                                      ) : (
                                        <Form.Control
                                          type="number"
                                          size="sm"
                                          step="0.01"
                                          value={item.unitPrice}
                                          onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                                          disabled={item.exclude}
                                        />
                                      )}
                                    </td>
                                    <td className="text-end fw-bold">
                                      ${(parseFloat(item.totalPrice) || 0).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="table-primary">
                                  <td colSpan="5" className="text-end fw-bold">
                                    Selected Items Total:
                                  </td>
                                  <td className="text-end fw-bold">
                                    ${includedItemsTotal.toFixed(2)}
                                  </td>
                                </tr>
                              </tfoot>
                            </Table>
                          </div>
                        </>
                      ) : (
                        <Alert variant="warning">
                          No items were extracted from this receipt. 
                          Please try a clearer photo.
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {scanResult ? (
            <>
              <Button variant="secondary" onClick={resetModal}>
                <FaTimes className="me-1" />
                Cancel
              </Button>
              <Button variant="outline-primary" onClick={() => {
                setScanResult(null);
                setEditableItems([]);
                setPreviewUrl(null);
                setIsPdfFile(false);
                setFileName('');
              }}>
                <FaCamera className="me-1" />
                Scan Another
              </Button>
              <Button 
                variant="success" 
                onClick={handleApprove}
                disabled={editableItems.filter(i => !i.exclude).length === 0}
              >
                <FaCheck className="me-1" />
                Add {editableItems.filter(i => !i.exclude).length} Materials
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={resetModal}>
              Cancel
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ReceiptScanner;
